import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

/**
 * "QR & graphics" tab on the event editor (Evan 2026-11-02: "in events manager i need a QR
 * generator and graphic generator for event flyers").
 *
 * Everything renders in the browser — no server, nothing stored. QR via qrcode.react (already a
 * dependency); graphics are drawn on a <canvas> from the event's flyer + the same wordmark, App
 * Store badge and Clash Display type as the public page, then downloaded as PNG.
 *
 * ⚠️ The flyer is loaded with crossOrigin="anonymous" — Supabase public storage sends
 * Access-Control-Allow-Origin: *, which is what lets canvas.toBlob() export instead of throwing
 * a tainted-canvas SecurityError.
 */

const inputCls =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-white placeholder:text-mutedDim focus:border-accent focus:outline-none';
const labelCls = 'mb-1 block text-[10.5px] uppercase tracking-[0.08em] text-mutedDim';
const pill = (on: boolean) =>
  `rounded-full border px-3 py-1.5 text-[12.5px] font-medium ${
    on ? 'border-accent bg-accent/15 text-accent' : 'border-border text-muted hover:text-white'
  }`;

const VIOLET = '#8B5CF6';
const VIOLET_TEXT = '#AF8FFA';

type QrStyle = 'dark' | 'light' | 'purple';
const QR_STYLES: Record<QrStyle, { label: string; fg: string; bg: string }> = {
  light: { label: 'Black on white', fg: '#08090A', bg: '#FFFFFF' },
  dark: { label: 'White on black', fg: '#FFFFFF', bg: '#000000' },
  purple: { label: 'Purple on white', fg: '#6D3FE0', bg: '#FFFFFF' },
};

// Graphic QR customization (Evan 2026-11-03: "make it where I can remove the plate and/or text
// from QR / customize those elements too and also change position of the QR").
type Plate = 'white' | 'black' | 'purple' | 'none';
const PLATES: Record<Plate, { label: string; fill: string | null }> = {
  white: { label: 'White', fill: '#FFFFFF' },
  black: { label: 'Black', fill: '#08090A' },
  purple: { label: 'Purple', fill: '#6D3FE0' },
  none: { label: 'No plate', fill: null },
};
type Dots = 'black' | 'white' | 'purple';
const DOTS: Record<Dots, { label: string; c: string }> = {
  black: { label: 'Black', c: '#08090A' },
  white: { label: 'White', c: '#FFFFFF' },
  purple: { label: 'Purple', c: '#6D3FE0' },
};
type CtaColor = 'auto' | 'purple' | 'white' | 'black';
const CTA_COLORS: Record<CtaColor, { label: string; c: string | null }> = {
  auto: { label: 'Auto', c: null },
  purple: { label: 'Purple', c: '#5B2FD0' },
  white: { label: 'White', c: '#FFFFFF' },
  black: { label: 'Black', c: '#08090A' },
};
type QrPos = 'br' | 'bl' | 'tr' | 'tl' | 'c';
const QR_POS: Record<QrPos, string> = {
  tl: 'Top left',
  tr: 'Top right',
  c: 'Center',
  bl: 'Bottom left',
  br: 'Bottom right',
};

type Format = 'story' | 'post' | 'square' | 'print';
const FORMATS: Record<Format, { label: string; w: number; h: number; hint: string }> = {
  story: { label: 'Story', w: 1080, h: 1920, hint: 'Instagram / Snapchat story' },
  post: { label: 'Post', w: 1080, h: 1350, hint: 'Instagram feed (4:5)' },
  square: { label: 'Square', w: 1080, h: 1080, hint: 'Feed / GroupMe' },
  print: { label: 'Print', w: 2550, h: 3300, hint: '8.5×11 in at 300 dpi' },
};

function loadImg(src: string, cors = false): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    if (cors) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function download(href: string, name: string) {
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Draw `text` with extra letter spacing (canvas letterSpacing isn't in every browser). */
function trackedText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, track: number) {
  let cx = x;
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + track;
  }
  return cx - x - track;
}
// ── Styled QR drawing ───────────────────────────────────────────────────────────────────────
// qrcode.react doesn't expose the module matrix, so it's read back off the hidden transparent
// canvas (marginSize 0): the top-left finder is 7 modules wide, which gives the cell size.
type QrBody = 'square' | 'rounded' | 'dots';
type QrEyes = 'square' | 'rounded' | 'circle';
const QR_BODIES: Record<QrBody, string> = { square: 'Square', rounded: 'Rounded', dots: 'Dots' };
const QR_EYES: Record<QrEyes, string> = { square: 'Square', rounded: 'Rounded', circle: 'Circle' };

function readQrMatrix(src: HTMLCanvasElement): boolean[][] | null {
  const ctx = src.getContext('2d');
  if (!ctx) return null;
  const S = src.width;
  const data = ctx.getImageData(0, 0, S, S).data;
  const dark = (x: number, y: number) => data[(Math.floor(y) * S + Math.floor(x)) * 4 + 3] > 128;
  const row = Math.max(1, Math.floor(S / 200));
  let x = 0;
  while (x < S && dark(x, row)) x++;
  if (x === 0 || x >= S) return null;
  const n = Math.round((S * 7) / x);
  const c = S / n;
  const m: boolean[][] = [];
  for (let j = 0; j < n; j++) {
    const r: boolean[] = [];
    for (let i = 0; i < n; i++) r.push(dark((i + 0.5) * c, (j + 0.5) * c));
    m.push(r);
  }
  return m;
}

function drawStyledQr(
  ctx: CanvasRenderingContext2D,
  m: boolean[][],
  x: number,
  y: number,
  size: number,
  color: string,
  body: QrBody,
  eyes: QrEyes,
) {
  const n = m.length;
  const c = size / n;
  const inEye = (i: number, j: number) => (i < 7 && j < 7) || (i >= n - 7 && j < 7) || (i < 7 && j >= n - 7);
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      if (!m[j][i] || inEye(i, j)) continue;
      const px = x + i * c;
      const py = y + j * c;
      if (body === 'square') ctx.rect(px, py, c + 0.4, c + 0.4);
      else if (body === 'rounded') ctx.roundRect(px + c * 0.03, py + c * 0.03, c * 0.94, c * 0.94, c * 0.2);
      else {
        ctx.moveTo(px + c * 0.92, py + c / 2);
        ctx.arc(px + c / 2, py + c / 2, c * 0.42, 0, Math.PI * 2);
      }
    }
  }
  ctx.fill();
  for (const [ei, ej] of [
    [0, 0],
    [n - 7, 0],
    [0, n - 7],
  ]) {
    const ex = x + ei * c;
    const ey = y + ej * c;
    ctx.beginPath();
    if (eyes === 'circle') {
      ctx.arc(ex + 3.5 * c, ey + 3.5 * c, 3.5 * c, 0, Math.PI * 2);
      ctx.arc(ex + 3.5 * c, ey + 3.5 * c, 2.5 * c, 0, Math.PI * 2, true);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(ex + 3.5 * c, ey + 3.5 * c, 1.5 * c, 0, Math.PI * 2);
      ctx.fill();
    } else {
      const r = eyes === 'rounded' ? [2.1 * c, 1.4 * c, 0.9 * c] : [0, 0, 0];
      ctx.roundRect(ex, ey, 7 * c, 7 * c, r[0]);
      ctx.roundRect(ex + c, ey + c, 5 * c, 5 * c, r[1]);
      ctx.fill('evenodd');
      ctx.beginPath();
      ctx.roundRect(ex + 2 * c, ey + 2 * c, 3 * c, 3 * c, r[2]);
      ctx.fill();
    }
  }
}

function trackedWidth(ctx: CanvasRenderingContext2D, text: string, track: number) {
  let w = 0;
  for (const ch of text) w += ctx.measureText(ch).width + track;
  return w - track;
}

/**
 * useState that survives refreshes (Evan 2026-11-03: "everytime i refresh admin dash my qr and
 * graphic studio settings reset, they should retain session to session"). Saved per event in
 * localStorage; a blocked/private storage just falls back to the default.
 */
function usePersisted<T>(slug: string, key: string, initial: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const storeKey = `shado:gfx:${slug}:${key}`;
  const [v, setV] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(storeKey);
      return raw != null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(storeKey, JSON.stringify(v));
    } catch {
      /* storage blocked */
    }
  }, [storeKey, v]);
  return [v, setV];
}

/** Runs `fn` when `dep` changes AFTER mount — so an event edit refreshes a field without
 *  overwriting the saved value on every page load. */
function useOnChange<T>(dep: T, fn: () => void) {
  const prev = useRef(dep);
  useEffect(() => {
    if (prev.current !== dep) {
      prev.current = dep;
      fn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);
}

// Browsers cap canvas area (Chrome ~268M px, Safari ~16.7M px) — keep exports under ~36M px.
const QUALITIES: { q: number; label: string }[] = [
  { q: 1, label: 'Standard 1×' },
  { q: 2, label: 'High 2×' },
  { q: 3, label: 'Max 3×' },
];
function maxQualityFor(w: number, h: number) {
  let q = 3;
  while (q > 1 && w * h * q * q > 36_000_000) q--;
  return q;
}

function Slider({
  label,
  value,
  min,
  max,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  unit?: string;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <label className={labelCls}>
        {label} · {value}
        {unit ?? ''}
      </label>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#8B5CF6]"
      />
    </div>
  );
}

export function EventGraphicsTab({
  slug,
  title,
  dateLine,
  flyerUrl,
}: {
  slug: string;
  title: string;
  dateLine: string | null;
  flyerUrl: string | null;
}) {
  const eventUrl = `https://events.joinshado.com/${slug}`;

  // ── QR ────────────────────────────────────────────────────────────────────────────────────
  const [qrLink, setQrLink] = usePersisted(slug, 'qrLink', eventUrl);
  const [qrStyle, setQrStyle] = usePersisted<QrStyle>(slug, 'qrStyle', 'light');
  const qrWrap = useRef<HTMLDivElement>(null);
  const qrHiRes = useRef<HTMLDivElement>(null);
  useOnChange(eventUrl, () => setQrLink(eventUrl));

  const qrPng = () => {
    const c = qrHiRes.current?.querySelector('canvas');
    if (c) download(c.toDataURL('image/png'), "Shado-Event-QR.png");
  };

  // ── Graphic ───────────────────────────────────────────────────────────────────────────────
  const [format, setFormat] = usePersisted<Format>(slug, 'format', 'story');
  const [eyebrow, setEyebrow] = usePersisted(slug, 'eyebrow', 'SHADO PRESENTS');
  const [headline, setHeadline] = usePersisted(slug, 'headline', title);
  const [subline, setSubline] = usePersisted(slug, 'subline', dateLine ?? '');
  const [cta, setCta] = usePersisted(slug, 'cta', 'Scan to RSVP');
  const [showQr, setShowQr] = usePersisted(slug, 'showQr', true);
  const [showMark, setShowMark] = usePersisted(slug, 'showMark', true);
  const [showBadge, setShowBadge] = usePersisted(slug, 'showBadge', true);
  const [plate, setPlate] = usePersisted<Plate>(slug, 'plate', 'white');
  const [dots, setDots] = usePersisted<Dots>(slug, 'dots', 'black');
  const [qrBody, setQrBody] = usePersisted<QrBody>(slug, 'qrBody', 'square');
  const [qrEyes, setQrEyes] = usePersisted<QrEyes>(slug, 'qrEyes', 'square');
  const [showCta, setShowCta] = usePersisted(slug, 'showCta', true);
  const [ctaColor, setCtaColor] = usePersisted<CtaColor>(slug, 'ctaColor', 'auto');
  const [ctaScale, setCtaScale] = usePersisted(slug, 'ctaScale', 100); // % of the default 24px
  const [qrPos, setQrPos] = usePersisted<QrPos>(slug, 'qrPos', 'br');
  const [qrScale, setQrScale] = usePersisted(slug, 'qrScale', 100); // % of the default QR size
  const [nudgeX, setNudgeX] = usePersisted(slug, 'nudgeX', 0); // design px at 1080 wide
  const [nudgeY, setNudgeY] = usePersisted(slug, 'nudgeY', 0);
  const qrGfx = useRef<HTMLDivElement>(null);
  const [quality, setQuality] = usePersisted<number>(slug, 'quality', 2);
  const [rendering, setRendering] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useOnChange(title, () => setHeadline(title));
  useOnChange(dateLine, () => setSubline(dateLine ?? ''));

  // q = export multiplier: the preview draws at 1×, "High"/"Max" exports redraw the same layout
  // on a 2×/3× canvas (every measurement already scales with u = W / 1080).
  const drawTo = useCallback(async (canvas: HTMLCanvasElement | null, q = 1) => {
    if (!canvas) return;
    setRendering(true);
    setErr(null);
    const W = FORMATS[format].w * q;
    const H = FORMATS[format].h * q;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const u = W / 1080; // design unit: everything below is authored for a 1080-wide canvas

    try {
      await Promise.all([
        document.fonts.load(`700 ${Math.round(96 * u)}px "Clash Display"`),
        document.fonts.load(`500 ${Math.round(40 * u)}px "Clash Display"`),
        document.fonts.load(`600 ${Math.round(28 * u)}px "Clash Display"`),
      ]);
    } catch {
      /* falls back to Inter/system — still exports */
    }
    const [flyer, mark, badge] = await Promise.all([
      flyerUrl ? loadImg(flyerUrl, true) : Promise.resolve(null),
      showMark ? loadImg('/shado-wordmark.svg') : Promise.resolve(null),
      showBadge ? loadImg('/app-store-badge.svg') : Promise.resolve(null),
    ]);

    // Background: flyer, cover-fit, anchored to the top so the art (not the floor) survives a tall crop
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
    if (flyer) {
      const s = Math.max(W / flyer.width, H / flyer.height);
      const dw = flyer.width * s;
      const dh = flyer.height * s;
      ctx.drawImage(flyer, (W - dw) / 2, H > W ? 0 : (H - dh) / 2, dw, dh);
      if (H > W && dh < H + 1) {
        /* cover-fit always fills; nothing to do */
      }
    } else {
      const g = ctx.createRadialGradient(W / 2, 0, 0, W / 2, 0, H);
      g.addColorStop(0, '#3b2370');
      g.addColorStop(1, '#000');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);
    }

    // Bottom scrim so the type always reads
    const scrimTop = H * (H > W ? 0.5 : 0.42);
    const sg = ctx.createLinearGradient(0, scrimTop, 0, H);
    sg.addColorStop(0, 'rgba(0,0,0,0)');
    sg.addColorStop(0.45, 'rgba(0,0,0,0.62)');
    sg.addColorStop(1, 'rgba(0,0,0,0.94)');
    ctx.fillStyle = sg;
    ctx.fillRect(0, scrimTop, W, H - scrimTop);

    const X = 64 * u;
    const top = 64 * u;

    // Wordmark top-left, App Store badge top-right, centred on the wordmark's midline (site layout)
    const markW = 210 * u;
    const markH = markW * (822 / 2904);
    if (mark) {
      ctx.globalAlpha = 0.92;
      ctx.drawImage(mark, X, top, markW, markH);
      ctx.globalAlpha = 1;
    }
    if (badge) {
      const bh = markH * (29 / 25);
      const bw = bh * (392 / 116);
      ctx.globalAlpha = 0.95;
      ctx.drawImage(badge, W - X - bw, top + markH / 2 - bh / 2, bw, bh);
      ctx.globalAlpha = 1;
    }

    // QR card: plate (or none), dot color, optional line under it, preset position + nudge
    const bottom = H - 64 * u;
    let textX = X;
    let qrBesideTitle = false; // QR in the bottom row squeezes the title, so it may wrap
    let textMaxW = W - 2 * X;
    if (showQr) {
      const qrSrc = qrGfx.current?.querySelector('canvas');
      const plateFill = PLATES[plate].fill;
      const qs = (H > W ? 260 : 220) * u * (qrScale / 100);
      const pad = plateFill ? 18 * u * (qrScale / 100) : 0;
      const ctaText = showCta ? cta.trim() : '';
      const ctaSize = 24 * u * (ctaScale / 100);
      const ctaGap = (plateFill ? 10 : 14) * u;
      const cardW = Math.max(qs + pad * 2, 0);
      const cardH = qs + pad * 2 + (ctaText ? ctaGap + ctaSize * 1.1 + (plateFill ? 4 * u : 0) : 0);
      const headerBottom = top + (mark || badge ? markH * (29 / 25) + 40 * u : 0);
      let cx: number;
      let cy: number;
      if (qrPos === 'br' || qrPos === 'tr') cx = W - X - cardW;
      else if (qrPos === 'c') cx = (W - cardW) / 2;
      else cx = X;
      if (qrPos === 'br' || qrPos === 'bl') cy = bottom - cardH;
      else if (qrPos === 'c') cy = (H - cardH) / 2;
      else cy = headerBottom;
      cx += nudgeX * u;
      cy += nudgeY * u;
      if (plateFill) {
        ctx.fillStyle = plateFill;
        ctx.beginPath();
        ctx.roundRect(cx, cy, cardW, cardH, 22 * u * (qrScale / 100));
        ctx.fill();
      }
      const matrix = qrSrc ? readQrMatrix(qrSrc) : null;
      if (matrix) {
        drawStyledQr(ctx, matrix, cx + pad, cy + pad, qs, DOTS[dots].c, qrBody, qrEyes);
      } else if (qrSrc) {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(qrSrc, cx + pad, cy + pad, qs, qs);
        ctx.imageSmoothingEnabled = true;
      }
      if (ctaText) {
        const auto = plate === 'white' ? '#5B2FD0' : '#FFFFFF';
        ctx.fillStyle = CTA_COLORS[ctaColor].c ?? auto;
        ctx.font = `600 ${ctaSize}px "Clash Display", Inter, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        if (!plateFill) {
          ctx.shadowColor = 'rgba(0,0,0,0.6)';
          ctx.shadowBlur = 12 * u;
        }
        ctx.fillText(ctaText, cx + cardW / 2, cy + pad + qs + ctaGap + ctaSize * 0.85);
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
        ctx.textAlign = 'left';
      }
      // Keep the title block clear of a QR that sits in the bottom row
      const qrInBottomRow = cy + cardH > H * 0.6;
      qrBesideTitle = qrInBottomRow;
      if (qrInBottomRow && cx > W / 2) {
        textMaxW = Math.max(200 * u, cx - X - 28 * u);
      } else if (qrInBottomRow && cx + cardW < W / 2 + 40 * u) {
        textX = cx + cardW + 28 * u;
        textMaxW = Math.max(200 * u, W - X - textX);
      }
    }

    // Text block bottom-left: eyebrow / headline (auto-shrinks, wraps to 2 lines) / subline
    ctx.textBaseline = 'alphabetic';
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 18 * u;
    let y = bottom;

    if (subline.trim()) {
      const ss = 40 * u;
      ctx.font = `500 ${ss}px "Clash Display", Inter, sans-serif`;
      let size = ss;
      while (ctx.measureText(subline.trim()).width > textMaxW && size > 22 * u) {
        size -= 1;
        ctx.font = `500 ${size}px "Clash Display", Inter, sans-serif`;
      }
      ctx.fillStyle = '#E7E3F2';
      ctx.fillText(subline.trim(), textX, y);
      y -= size * 0.78 + 14 * u; // headline sits tight on the date line
    }

    if (headline.trim()) {
      let size = (H > W ? 112 : 100) * u;
      const words = headline.trim().split(/\s+/);
      const track = () => size * 0.025;
      const lay = (): string[] => {
        ctx.font = `700 ${size}px "Clash Display", Inter, sans-serif`;
        const lines: string[] = [];
        let cur = '';
        for (const wd of words) {
          const t = cur ? `${cur} ${wd}` : wd;
          if (trackedWidth(ctx, t, track()) <= textMaxW || !cur) cur = t;
          else {
            lines.push(cur);
            cur = wd;
          }
        }
        if (cur) lines.push(cur);
        return lines;
      };
      // One line when it fits at a readable size (Evan 2026-11-03: "the shado after dark text should
      // be one line when there is no qr bottom right"); otherwise wrap to 2 lines and shrink.
      const start = size;
      ctx.font = `700 ${size}px "Clash Display", Inter, sans-serif`;
      while (trackedWidth(ctx, headline.trim(), track()) > textMaxW && size > start * (qrBesideTitle ? 0.8 : 0.5)) {
        size -= 1;
        ctx.font = `700 ${size}px "Clash Display", Inter, sans-serif`;
      }
      if (trackedWidth(ctx, headline.trim(), track()) > textMaxW) size = start;
      let lines = lay();
      while ((lines.length > 2 || lines.some((l) => trackedWidth(ctx, l, track()) > textMaxW)) && size > 40 * u) {
        size -= 2;
        lines = lay();
      }
      ctx.fillStyle = '#FFFFFF';
      for (let i = lines.length - 1; i >= 0; i--) {
        trackedText(ctx, lines[i], textX, y, track());
        y -= size * 0.98;
      }
      y += 4 * u; // eyebrow a hair closer to the headline
    }

    if (eyebrow.trim()) {
      const es = 28 * u;
      ctx.font = `600 ${es}px "Clash Display", Inter, sans-serif`;
      ctx.fillStyle = VIOLET_TEXT;
      trackedText(ctx, eyebrow.trim().toUpperCase(), textX, y, es * 0.24);
    }
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';

    try {
      canvas.toDataURL(); // throws here (not at download time) if the flyer tainted the canvas
    } catch {
      setErr("The flyer image couldn't be used for export (storage CORS). Re-upload the flyer and try again.");
    }
    setRendering(false);
    // qrLink/dots aren't read here, but they re-render the hidden QR canvas this draws from —
    // they're deps so the graphic redraws when the QR changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format, eyebrow, headline, subline, cta, showQr, showMark, showBadge, flyerUrl, qrLink, plate, dots, showCta, ctaColor, ctaScale, qrPos, qrScale, nudgeX, nudgeY, qrBody, qrEyes]);

  useEffect(() => {
    const t = window.setTimeout(() => void drawTo(canvasRef.current, 1), 120);
    return () => window.clearTimeout(t);
  }, [drawTo]);

  const savePng = async () => {
    const { w, h } = FORMATS[format];
    const q = Math.min(quality, maxQualityFor(w, h));
    const c = document.createElement('canvas');
    await drawTo(c, q);
    // the preview's rendering flag was flipped by drawTo — redraw the preview state after export
    setRendering(false);
    c.toBlob((b) => {
      if (!b) return;
      const url = URL.createObjectURL(b);
      download(url, `Shado-Event-Graphic-${FORMATS[format].label}${q > 1 ? `@${q}x` : ''}.png`);
      window.setTimeout(() => URL.revokeObjectURL(url), 4000);
    }, 'image/png');
  };

  const st = QR_STYLES[qrStyle];
  const fmt = FORMATS[format];

  return (
    <>
      {/* QR */}
      <div className="admin-card space-y-4">
        <div className="admin-eyebrow">QR code</div>
        <div className="flex flex-wrap items-start gap-6">
          <div
            ref={qrWrap}
            className="rounded-xl p-3"
            style={{ background: st.bg, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)' }}
          >
            <QRCodeCanvas value={qrLink || eventUrl} size={176} fgColor={st.fg} bgColor={st.bg} level="M" marginSize={0} />
          </div>
          <div className="min-w-[240px] flex-1 space-y-3">
            <div>
              <label className={labelCls}>Link</label>
              <input className={inputCls} value={qrLink} onChange={(e) => setQrLink(e.target.value)} />
              {qrLink !== eventUrl ? (
                <button type="button" className="mt-1 text-[11px] text-accent hover:underline" onClick={() => setQrLink(eventUrl)}>
                  Reset to event link
                </button>
              ) : null}
            </div>
            <div>
              <label className={labelCls}>Colors</label>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(QR_STYLES) as QrStyle[]).map((k) => (
                  <button key={k} type="button" className={pill(qrStyle === k)} onClick={() => setQrStyle(k)}>
                    {QR_STYLES[k].label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={qrPng}
              className="rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-black"
            >
              Download QR (PNG, 1200px)
            </button>
          </div>
        </div>
        {/* Hi-res copy used for the PNG download and drawn into the graphics. Off-screen, not display:none, so it paints. */}
        <div ref={qrHiRes} style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden>
          <QRCodeCanvas value={qrLink || eventUrl} size={1200} fgColor={st.fg} bgColor={st.bg} level="M" marginSize={2} />
        </div>
        {/* Transparent-background copy for the graphic: the plate (or the flyer) shows through. */}
        <div ref={qrGfx} style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden>
          <QRCodeCanvas value={qrLink || eventUrl} size={1200} fgColor={DOTS[dots].c} bgColor="rgba(0,0,0,0)" level="M" marginSize={0} />
        </div>
      </div>

      {/* Graphic */}
      <div className="admin-card mt-6 space-y-4">
        <div className="admin-eyebrow">Flyer graphic</div>
        <div className="flex flex-wrap gap-6">
          <div className="min-w-[260px] flex-1 space-y-3">
            <div>
              <label className={labelCls}>Size</label>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(FORMATS) as Format[]).map((k) => (
                  <button key={k} type="button" title={FORMATS[k].hint} className={pill(format === k)} onClick={() => setFormat(k)}>
                    {FORMATS[k].label}
                  </button>
                ))}
              </div>
              <div className="mt-1 text-[11px] text-mutedDim">
                {fmt.hint} · {fmt.w}×{fmt.h}
              </div>
            </div>
            <div>
              <label className={labelCls}>Small line above the title</label>
              <input className={inputCls} value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Title</label>
              <input className={inputCls} value={headline} onChange={(e) => setHeadline(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Line under the title</label>
              <input className={inputCls} value={subline} onChange={(e) => setSubline(e.target.value)} placeholder="Tuesday, November 17th @ Verve" />
            </div>
            {showQr ? (
              <div className="space-y-3 rounded-xl border border-border p-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">QR in the graphic</div>
                <div>
                  <label className={labelCls}>Position</label>
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(QR_POS) as QrPos[]).map((k) => (
                      <button
                        key={k}
                        type="button"
                        className={pill(qrPos === k)}
                        onClick={() => {
                          setQrPos(k);
                          setNudgeX(0);
                          setNudgeY(0);
                        }}
                      >
                        {QR_POS[k]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Slider label="Size" value={qrScale} min={50} max={200} unit="%" onChange={setQrScale} />
                  <Slider label="Nudge left / right" value={nudgeX} min={-400} max={400} onChange={setNudgeX} />
                  <Slider label="Nudge up / down" value={nudgeY} min={-600} max={600} onChange={setNudgeY} />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>QR style</label>
                    <div className="flex flex-wrap gap-2">
                      {(Object.keys(QR_BODIES) as QrBody[]).map((k) => (
                        <button key={k} type="button" className={pill(qrBody === k)} onClick={() => setQrBody(k)}>
                          {QR_BODIES[k]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Corner squares</label>
                    <div className="flex flex-wrap gap-2">
                      {(Object.keys(QR_EYES) as QrEyes[]).map((k) => (
                        <button key={k} type="button" className={pill(qrEyes === k)} onClick={() => setQrEyes(k)}>
                          {QR_EYES[k]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Plate</label>
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(PLATES) as Plate[]).map((k) => (
                      <button
                        key={k}
                        type="button"
                        className={pill(plate === k)}
                        onClick={() => {
                          setPlate(k);
                          // keep the code scannable: dark dots on a light plate, light dots otherwise
                          if (k === 'white' && dots === 'white') setDots('black');
                          if ((k === 'black' || k === 'purple' || k === 'none') && dots !== 'white') setDots('white');
                        }}
                      >
                        {PLATES[k].label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>QR color</label>
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(DOTS) as Dots[]).map((k) => (
                      <button key={k} type="button" className={pill(dots === k)} onClick={() => setDots(k)}>
                        {DOTS[k].label}
                      </button>
                    ))}
                  </div>
                  {plate === 'none' ? (
                    <div className="mt-1 text-[11px] text-yellow-300">
                      No plate: scan-test it — the code needs contrast against the flyer behind it.
                    </div>
                  ) : null}
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className={`${labelCls} mb-0`}>Text under the QR</label>
                    <button type="button" className={pill(showCta)} onClick={() => setShowCta((v) => !v)}>
                      {showCta ? 'On' : 'Off'}
                    </button>
                  </div>
                  <input className={inputCls} value={cta} onChange={(e) => setCta(e.target.value)} disabled={!showCta} />
                </div>
                {showCta ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>Text color</label>
                      <div className="flex flex-wrap gap-2">
                        {(Object.keys(CTA_COLORS) as CtaColor[]).map((k) => (
                          <button key={k} type="button" className={pill(ctaColor === k)} onClick={() => setCtaColor(k)}>
                            {CTA_COLORS[k].label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Slider label="Text size" value={ctaScale} min={60} max={200} unit="%" onChange={setCtaScale} />
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button type="button" className={pill(showQr)} onClick={() => setShowQr((v) => !v)}>
                QR code
              </button>
              <button type="button" className={pill(showMark)} onClick={() => setShowMark((v) => !v)}>
                shado wordmark
              </button>
              <button type="button" className={pill(showBadge)} onClick={() => setShowBadge((v) => !v)}>
                App Store badge
              </button>
            </div>
            {!flyerUrl ? (
              <div className="text-[12px] text-yellow-300">No flyer uploaded — the graphic uses a purple background.</div>
            ) : null}
            {err ? <div className="text-[12px] text-red-400">{err}</div> : null}
            <div>
              <label className={labelCls}>Export quality</label>
              <div className="flex flex-wrap gap-2">
                {QUALITIES.map(({ q, label }) => {
                  const ok = q <= maxQualityFor(fmt.w, fmt.h);
                  return (
                    <button
                      key={q}
                      type="button"
                      disabled={!ok}
                      title={ok ? undefined : 'Too large for the browser at this size'}
                      className={`${pill(quality === q)} disabled:opacity-40`}
                      onClick={() => setQuality(q)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 text-[11px] text-mutedDim">
                Exports at {fmt.w * Math.min(quality, maxQualityFor(fmt.w, fmt.h))}×
                {fmt.h * Math.min(quality, maxQualityFor(fmt.w, fmt.h))} px
              </div>
            </div>
            <button
              type="button"
              disabled={rendering || !!err}
              onClick={() => void savePng()}
              className="rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-black disabled:opacity-50"
            >
              {rendering ? 'Rendering…' : `Download ${fmt.label} PNG`}
            </button>
            <div className="text-[11px] text-mutedDim">The QR in the graphic uses the link above.</div>
          </div>
          <div className="shrink-0">
            <canvas
              ref={canvasRef}
              style={{
                width: fmt.w >= fmt.h ? 320 : Math.round((320 * fmt.w) / fmt.h) + 60,
                height: 'auto',
                borderRadius: 12,
                boxShadow: `0 0 0 1px rgba(255,255,255,0.08), 0 10px 40px ${VIOLET}22`,
                display: 'block',
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
